import { useEffect, useState, useRef } from "react";
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUploadImage } from '@src/components/node-info/hooks/useUploadImage.hook';
import { base64ToBlob } from "@/lib/utils";
import { Icon } from '@src/components/icon/Icon';


interface OptionCropMeta {
  width: number;
  height: number;
  x: number;
  y: number;
  unit: string;
  originUploadId?: string;
}

interface ImageEditModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  optionValue: string;
  optionUploadId: string;
  optionCropMeta?: OptionCropMeta;
  onReplace: (optionValue: string) => void;
  onSave?: (croppedImageData: string, optionValue: string, cropMeta: OptionCropMeta) => void;
}

export const ImageEditModal = ({
  isOpen,
  onOpenChange,
  optionValue,
  optionUploadId,
  optionCropMeta,
  onReplace,
  onSave
}: ImageEditModalProps) => {
  const { fetchImagesUrlById, fetchImageContentById } = useUploadImage();
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [imageBase64, setImageBase64] = useState<string | undefined>(undefined);
  const [cropSrc, setCropSrc] = useState<string | undefined>(undefined);
  const [crop, setCrop] = useState<Crop | undefined>(optionCropMeta ? {
    unit: (optionCropMeta?.unit) as 'px' | '%',
    width: optionCropMeta?.width,
    height: optionCropMeta?.height,
    x: optionCropMeta?.x,
    y: optionCropMeta?.y
  } : undefined);
  // no completedCrop local state needed; we only save on demand
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (optionCropMeta) {
      setCrop(optionCropMeta as Crop);
    }
  }, [optionCropMeta]);

  useEffect(() => {
    async function fetchImageUrl() {
      const url = await fetchImagesUrlById([optionUploadId], 'original', false);
      setImageUrl(url?.[optionUploadId]);
      const content = await fetchImageContentById([optionUploadId], 'original', false);
      setImageBase64(content?.[optionUploadId]);
    }
    fetchImageUrl().catch(e => console.error('Fetch image url failed', e));
  }, [optionUploadId]);

  useEffect(() => {
    if (!imageBase64) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const blob = base64ToBlob(imageBase64);
        setCropSrc(URL.createObjectURL(blob));
      } catch (e) {
        console.warn('Falling back to direct URL for crop source due to CORS', e);
        if (!cancelled) setCropSrc(imageUrl);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [imageBase64]);

  const handleReplace = () => {
    onReplace(optionValue);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const getCroppedImg = (image: HTMLImageElement, crop: PixelCrop): Promise<string> => {
    const canvas = canvasRef.current;
    if (!canvas) {
      throw new Error('Canvas ref not available');
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas context not available');
    }

    canvas.width = crop.width;
    canvas.height = crop.height;

    let cropX = crop.x;
    let cropY = crop.y;

    if (!crop.width || !crop.height) {
      canvas.width = image.width;
      canvas.height = image.height;
      cropX = 0;
      cropY = 0;
    }

    ctx.drawImage(
      image,
      cropX * scaleX,
      cropY * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          throw new Error('Canvas is empty');
        }
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      }, 'image/jpeg', 0.95);
    });
  };

  const handleSave = async () => {
    if (crop && imgRef.current && onSave) {
      try {
        const imageEl = imgRef.current;
        const croppedImageData = await getCroppedImg(imageEl, crop as PixelCrop);

        let adjustedMeta: OptionCropMeta;

        if (!crop.width || !crop.height) {
          adjustedMeta = {
            unit: 'px',
            x: 0,
            y: 0,
            width: imageEl.naturalWidth,
            height: imageEl.naturalHeight,
            originUploadId: optionUploadId,
          };
        } else if (crop.unit === '%') {
          adjustedMeta = {
            unit: '%',
            x: crop.x,
            y: crop.y,
            width: crop.width,
            height: crop.height,
            originUploadId: optionUploadId,
          };
        } else {
          const scaleX = imageEl.naturalWidth / imageEl.width;
          const scaleY = imageEl.naturalHeight / imageEl.height;

          adjustedMeta = {
            unit: 'px',
            x: Math.round(crop.x * scaleX),
            y: Math.round(crop.y * scaleY),
            width: Math.round(crop.width * scaleX),
            height: Math.round(crop.height * scaleY),
            originUploadId: optionUploadId,
          };
        }

        onSave(croppedImageData, optionValue, adjustedMeta);
        onOpenChange(false);
      } catch (error) {
        console.error('Error cropping image:', error);
      }
    } else {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange} key={optionValue}>
      <DialogContent className="max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Edit image</DialogTitle>
        </DialogHeader>
        <div className="w-full">
          {cropSrc ? (
            <>
              <ReactCrop
                key={optionValue}
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCrop(c)}
                aspect={1}
                className="rounded-lg"
              >
                <img
                  key={optionValue}
                  ref={imgRef}
                  src={(cropSrc)}
                  alt="Crop preview"
                  crossOrigin="anonymous"
                  style={{ maxWidth: '50vw', borderRadius: 10 }}
                  onLoad={(e) => {
                    const image = e.currentTarget as HTMLImageElement;

                    if (!optionCropMeta) {
                      return;
                    }

                    const unit = (optionCropMeta.unit as 'px' | '%') ?? 'px';

                    // If meta provided in percentages, it already scales with display size
                    if (unit === '%') {
                      setCrop(optionCropMeta as Crop);
                      return;
                    }

                    // Convert original (natural) pixel crop to displayed pixel crop
                    const scaleX = image.width / image.naturalWidth;
                    const scaleY = image.height / image.naturalHeight;

                    setCrop({
                      unit: 'px',
                      x: Math.round(optionCropMeta.x * scaleX),
                      y: Math.round(optionCropMeta.y * scaleY),
                      width: Math.round(optionCropMeta.width * scaleX),
                      height: Math.round(optionCropMeta.height * scaleY),
                    } as Crop);
                  }}
                />
              </ReactCrop>
              <canvas
                ref={canvasRef}
                style={{ display: 'none' }}
              />
            </>
          ) : (
            <div>
              <div className="flex flex-col justify-center items-center px-3 py-2 w-[50vw] h-[50vh]">
                <Icon svgProps={{ width: 300, height: 300, color: ' hsla(240, 12%, 48%, 0.08)' }} type="image" className="animate-pulse w-[400px] h-[400px] text-gray-300" />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" size="lg" className="mr-auto px-4" onClick={handleReplace}>
            <Icon type="image" className="w-4 h-4 mr-1" />
            Replace
          </Button>
          <Button className="px-6" variant="secondary" size="lg" onClick={handleCancel}>Discard</Button>
          <Button className="px-6" font="thin" variant="action" size="lg" onClick={() => void handleSave()}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
